import type { JSX, ReactNode } from 'react';
import { QueryBoundary } from './query-boundary';
import type { QueryLike } from './query-state';

/**
 * The panel every portal renders a query through: exactly one of pending,
 * failed, empty or content, with the app's own cards bound ONCE.
 *
 * WHAT IT OWNS. The composition over `QueryBoundary` - which arm is drawn, that
 * the failure arm is handed a retry that refetches, that the empty test is asked
 * beside the other three rather than nested inside the content. Every screen used
 * to spell those four lines out - a `pending` arm, an `error` arm with its own
 * `refetch`, a `ready` arm whose first act was a second `length === 0` test -
 * which is three chances per screen to leave one out. And three portals then
 * wrote this composition each, identical but for the cards; this is that
 * composition, once, and each app's `AsyncPanel` is a one-line binding of it.
 *
 * WHY THE PHASE AND NOT `isPending`. The decision is `queryPhase`
 * (`query-state.ts`), with the deadline in `useStalled`. A query the retryer
 * PARKED at `fetchStatus: 'paused'` is `pending` for ever and carries no error,
 * so a panel reading `isPending || data === undefined` showed its spinner and
 * never reached the failure card three lines below; a guest sat on one for 90+
 * seconds (TST-001). Paused, cancelled and past-the-deadline all read as
 * failures here, and the ready arm is handed its data, so the fall-through to a
 * second spinner cannot be written.
 *
 * THE ERROR CARD IS A REQUIRED ARGUMENT. `AsyncPanelCards.error` has no default:
 * a binding cannot leave the failure arm out, which is the whole reason
 * `QueryBoundary` exists. The card's words, the request id and the way back
 * (sign in again, rescan the code) belong to the app; that one exists does not.
 *
 * WHAT IT REFUSES. Words - every card arrives already translated, because this
 * package carries no catalogue. And a second decision: an app does not declare
 * an `AsyncPanel` of its own other than by `createAsyncPanel(`, which
 * scripts/check-no-eternal-spinner.mjs holds it to.
 */

export interface AsyncPanelProps<T> {
  /** The query result, narrowed to what a panel needs, plus its own retry. */
  readonly query: QueryLike<T> & { refetch(): unknown };
  readonly children: (data: T) => ReactNode;
  /** True when the answer arrived and has nothing in it. */
  readonly emptyWhen?: (data: T) => boolean;
  /** Handed to the app's empty card, which decides what to do with it. */
  readonly empty?: ReactNode;
  /** The query is switched off by its own precondition, not by the network. */
  readonly disabled?: boolean;
  /** Shown while resolving instead of the bound pending card (a skeleton of the table that is coming). */
  readonly pending?: ReactNode;
  /** A screen's own failure arm, when the bound card is not the way out; it still receives the retry. */
  readonly error?: (failure: unknown, retry: () => void) => ReactNode;
}

/** The app's three cards. The error card is REQUIRED: a binding cannot leave the failure arm out. */
export interface AsyncPanelCards {
  readonly pending: ReactNode;
  /** Given the screen's `empty` node, or `undefined` when it passed none. */
  readonly empty: (custom: ReactNode | undefined) => ReactNode;
  readonly error: (failure: unknown, retry: () => void) => ReactNode;
}

/** What `createAsyncPanel` hands back: the app's `AsyncPanel` component. */
export type AsyncPanelComponent = <T>(props: AsyncPanelProps<T>) => JSX.Element;

/** Renders exactly one of pending, failed, empty or content over `QueryBoundary`, with these cards. */
export function createAsyncPanel(cards: AsyncPanelCards): AsyncPanelComponent {
  return function AsyncPanel<T>({
    query,
    children,
    emptyWhen,
    empty,
    disabled,
    pending,
    error,
  }: AsyncPanelProps<T>): JSX.Element {
    const retry = (): void => {
      void query.refetch();
    };
    const failed = error ?? cards.error;
    return (
      <QueryBoundary
        query={query}
        {...(emptyWhen === undefined ? {} : { isEmpty: emptyWhen })}
        {...(disabled === undefined ? {} : { disabled })}
        pending={pending ?? cards.pending}
        empty={cards.empty(empty)}
        error={(failure) => failed(failure, retry)}
      >
        {children}
      </QueryBoundary>
    );
  };
}
